// Reusable bet tag badge.
//
// Renders the deterministic tag returned by the backend's
// ``services/bet_tags.py``. The tag object shape is:
//   { tag: "VALUE" | "PREMIUM" | "TRAP",
//     icon, label, color, weight, edge_pct }
//
// Usage:
//   <BetTag tag={bet.tag} />          // small inline badge
//   <BetTag tag={bet.tag} size="lg"/> // chunky version for hero cards
//
// Falls back gracefully to ``null`` when the bet has no tag.

import { memo } from "react";
import { playSfx } from "@/lib/audio";

const SIZES = {
  sm: { padding: "0.18rem 0.4rem", fontSize: "0.6rem",  iconSize: "0.7rem", letter: "0.16em" },
  md: { padding: "0.24rem 0.55rem", fontSize: "0.66rem", iconSize: "0.8rem", letter: "0.18em" },
  lg: { padding: "0.32rem 0.7rem", fontSize: "0.78rem", iconSize: "0.95rem", letter: "0.22em" },
};

function BetTagComponent({ tag, size = "md", showEdge = true, className = "" }) {
  if (!tag || !tag.tag) return null;
  const sz = SIZES[size] || SIZES.md;
  const color = tag.color || "#10B981";
  return (
    <span
      data-testid={`bet-tag-${tag.tag.toLowerCase()}`}
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.3rem",
        padding: sz.padding,
        background: `color-mix(in srgb, ${color} 12%, var(--bc-panel, #09090B))`,
        border: `1px solid ${color}`,
        boxShadow: `0 0 12px -4px ${color}, inset 0 0 8px -4px ${color}`,
        color: color,
        fontFamily: "'Barlow Condensed',sans-serif",
        fontSize: sz.fontSize,
        fontWeight: 800,
        letterSpacing: sz.letter,
        textTransform: "uppercase",
        lineHeight: 1,
        whiteSpace: "nowrap",
      }}
      title={`Edge ${tag.edge_pct >= 0 ? "+" : ""}${tag.edge_pct}%`}
      onMouseEnter={tag.tag === "TRAP" ? () => playSfx("warn") : undefined}
    >
      <span style={{ fontSize: sz.iconSize }} aria-hidden="true">{tag.icon}</span>
      <span>{tag.label}</span>
      {showEdge && typeof tag.edge_pct === "number" && (
        <span style={{
          opacity: 0.6, fontFamily: "'JetBrains Mono',monospace",
          fontSize: `calc(${sz.fontSize} - 0.06rem)`, letterSpacing: "0.05em",
        }}>
          {tag.edge_pct >= 0 ? "+" : ""}{tag.edge_pct.toFixed(1)}%
        </span>
      )}
    </span>
  );
}

export const BetTag = memo(BetTagComponent);
export default BetTag;
